import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';

/**
 * The three states every analytics panel has to survive: still loading, failed,
 * and "the query ran and there is genuinely nothing there". Kept in one place so
 * an empty period never reads as a broken screen.
 */
export function Panel({
  title,
  description,
  action,
  loading,
  failed,
  empty,
  onRetry,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  loading?: boolean;
  failed?: boolean;
  empty?: boolean;
  onRetry?: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent>
        {loading ? (
          <LoadingState label={t('common.loading')} />
        ) : failed ? (
          <ErrorState
            message={t('reports.errors.load')}
            onRetry={onRetry}
          />
        ) : empty ? (
          <EmptyState
            title={t('reports.empty.title')}
            hint={t('reports.empty.hint')}
          />
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A headline number. `hint` carries the secondary reading (a comparison, a
 * count) so the big figure never has to be two things at once.
 */
export function KpiTile({
  label,
  value,
  hint,
  muted,
}: {
  label: string;
  value: string;
  hint?: string;
  muted?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-sm font-medium text-ink-muted">{label}</p>
        <p
          className={
            muted
              ? 'mt-2 text-2xl font-semibold text-ink-muted'
              : 'mt-2 text-2xl font-semibold'
          }
        >
          {value}
        </p>
        {hint && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
      </CardContent>
    </Card>
  );
}

interface TipEntry {
  name?: string | number;
  value?: string | number;
  color?: string;
  dataKey?: string | number;
}

/**
 * Recharts' default tooltip prints raw numbers; this one runs every value back
 * through the locale formatter the tiles use, so 1 234,50 SAR reads the same
 * in the chart as in the table under it.
 */
export function ChartTip({
  active,
  payload,
  label,
  formatValue,
  formatLabel,
}: {
  active?: boolean;
  payload?: TipEntry[];
  label?: string | number;
  formatValue: (key: string, value: string | number | undefined) => string;
  formatLabel?: (label: string | number | undefined) => string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border bg-surface px-3 py-2 text-xs shadow-sm">
      <p className="mb-1 font-medium">{formatLabel ? formatLabel(label) : label}</p>
      {payload.map((entry) => (
        <p key={String(entry.dataKey)} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: entry.color }}
            aria-hidden
          />
          <span className="text-ink-muted">{entry.name}</span>
          <span className="ms-auto font-medium">
            {formatValue(String(entry.dataKey), entry.value)}
          </span>
        </p>
      ))}
    </div>
  );
}

/**
 * A bar drawn in CSS rather than a chart library: for a short ranked list it
 * reads better than a chart and keeps the value directly beside its label.
 */
export function MeterRow({
  label,
  value,
  share,
  color,
}: {
  label: string;
  value: string;
  share: number;
  color: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="truncate">{label}</span>
        <span className="shrink-0 font-medium tabular-nums">{value}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--muted)]">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(1, Math.min(100, share * 100))}%`, background: color }}
        />
      </div>
    </div>
  );
}
